import { DemandPlanningFilters } from '../../components/demandplanning/DemandPlanningFilters';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import DemandPlanningHeaderToolbar from '../../components/demandplanning/DemandPlanningHeaderToolbar';
import DemandPlanningColumnsSettings from '../../components/demandplanning/DemandPlanningColumnsSettings';
import ViewGovernanceDialog from '../../components/demandplanning/ViewGovernanceDialog';
import { productCatalogStatuses } from '../../consts/productCatalog';
import {
  isDefaultPlanningTableView,
  DEMAND_PLANNING_VIEW_PRESETS,
  PRESET_COLUMN_IDS,
  getVisibleDemandPlanningColumnIds,
  dedupeDemandPlanningColumnIds,
  buildDemandPlanningColumnDrawerRows,
  isDemandPlanningColumnLayoutCustomized,
  getDemandPlanningColumnLabel,
} from '../../consts/demandplanning';
import { fetchProductCatalogCategorySettings, ProductCatalogLoadingKeys } from '../../slices/productCatalogSlice';
import {
  DemandPlanningLoadingKeys,
  fetchForecastDemandPlanningList,
  createForecastDemandPlanning,
  updateForecastDemandPlanning,
  deleteForecastDemandPlanning,
  refreshForecastDemandSalesSnapshot,
  getEligibilityValidationTableData,
  getForecastBySkuTableData,
  getOpenToBuyTableData,
  getSkuAllocationTableData,
  clearDemandPlanningTableData,
  setForecastDemandPlanningData,
} from '../../slices/demandPlanningSlice';
import { useDispatch, useSelector } from 'react-redux';
import { ForecastDemandTable } from '../../components/demandplanning/ForecastDemandTable';
import DefaultPlanningTable from '../../components/demandplanning/DefaultPlanningTable';
import SkuAllocationUndoRestoreModal from '../../components/demandplanning/SkuAllocationUndoRestoreModal';
import axios from 'axios';
import { toast } from 'react-toastify';
import AiRebalanceTable from '../../components/demandplanning/AiRebalanceTable';
import ApprovedBuysTable from '../../components/demandplanning/ApprovedBuysTable';
import DemandDistributionProfileTable from '../../components/demandplanning/DemandDistributionProfileTable';
import { ArrowPathIcon, ArrowUturnLeftIcon, EllipsisVerticalIcon, EyeIcon, InformationCircleIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { TanStackTable } from '../../components/TanStackTable';
import ForecastDemandPlanningSidebar from '../../components/demandplanning/ForecastDemandPlanningSidebar';
import { ConfrimModal } from '../../components/ConfrimModal';
import { store } from '../../store';
import { useSearchParams } from 'react-router-dom';
import { formatForecastDemandPlanningCreatedDisplay } from '../../helpers/forecastDemandPlanningFormat';
import { LoaderSpinner } from '../../components/LoaderSpinner';
import CashLiabilitiesTable from '../../components/demandplanning/CashLiabilitiesTable';
import UpcomingBuysTable from '../../components/demandplanning/UpcomingBuysTable';
import POScheduleTable from '../../components/demandplanning/POScheduleTable';
import BundleDemandExplosionTable from '../../components/demandplanning/BundleDemandExplosionTable';
import BundleReplacementShiftTable from '../../components/demandplanning/BundleReplacementShiftTable';
import ReplacementDemandShiftTable from '../../components/demandplanning/ReplacementDemandShiftTable';
import PurchaseRoundingTable from '../../components/demandplanning/PurchaseRoundingTable';
import ContainerOptimizationTable from '../../components/demandplanning/ContainerOptimizationTable';
import InventoryCapitalPlanTable from '../../components/demandplanning/InventoryCapitalPlanTable';
import ComponentDemandLedgerTable from '../../components/demandplanning/ComponentDemandLedgerTable';
import { getDemandPlanningViewPermissionId } from '../../components/Sidebar/const';
import { getAllowedUserPermissions } from '../../helpers/common';
import { CHILDREN_TAB_DEMAND_PLAN_SHEET_IMPORT, isAdminRole } from '../../consts/management/users/users';
import ForecastDemandSheetImportModal from '../../components/planning/ForecastDemandSheetImportModal';

const { useState, useCallback, useMemo, useEffect, useRef } = require('react');

const formatForecastRevenueDisplay = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n);
};

const parseCommaSeparatedParam = (value) => {
  if (!value) return [];
  return value.split(',').filter(Boolean);
};

const filterArraysEqual = (a, b) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

const applyFilterParamsToSearchParams = (
  next,
  { searchTerm, selectedCategoryId, selectedSubcategoryId, selectedStatusId }
) => {
  if (searchTerm) next.set('search', searchTerm);
  else next.delete('search');
  if (selectedCategoryId.length) next.set('categories', selectedCategoryId.join(','));
  else next.delete('categories');
  if (selectedSubcategoryId.length) next.set('subcategories', selectedSubcategoryId.join(','));
  else next.delete('subcategories');
  if (selectedStatusId.length) next.set('statuses', selectedStatusId.join(','));
  else next.delete('statuses');
};

const clearFilterParamsFromSearchParams = (next) => {
  next.delete('search');
  next.delete('categories');
  next.delete('subcategories');
  next.delete('statuses');
};

/** List rows omit `data`; only keep loaded `data` when re-merging the same planning id. */
const selectForecastDemandPlanningRow = (row, { preserveDataFrom = null } = {}) => {
  if (!row) return null;
  const hasRowData = row.data != null && typeof row.data === 'object';
  const canPreserveData =
    !hasRowData &&
    preserveDataFrom &&
    String(preserveDataFrom.id) === String(row.id) &&
    preserveDataFrom.data != null &&
    typeof preserveDataFrom.data === 'object';
  return {
    ...row,
    data: hasRowData ? row.data : canPreserveData ? preserveDataFrom.data : null,
  };
};

const DemandPlanning = ({
  access,
  marginFromSidebar,
  allowedPermissionIds,
}) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const idFromUrl = searchParams.get('id');
  const viewFromUrl = searchParams.get('view');
  const sheetImportFromUrl = searchParams.get('import') === 'sheet';
  const getViewPermissionState = useMemo(() => {
    const hasPermissionSet = allowedPermissionIds instanceof Set;
    const isViewAllowed = (viewId) =>
      !hasPermissionSet || allowedPermissionIds.has(getDemandPlanningViewPermissionId(viewId));
    const firstAllowedView = DEMAND_PLANNING_VIEW_PRESETS.find((preset) => isViewAllowed(preset.id));

    return (viewId) => ({
      nextViewId:
        DEMAND_PLANNING_VIEW_PRESETS.some((preset) => preset.id === viewId) && isViewAllowed(viewId)
          ? viewId
          : firstAllowedView?.id,
    });
  }, [allowedPermissionIds]);
  const urlHydratedForIdRef = useRef(null);
  const listFetchRetryInFlightRef = useRef(null);
  const { categorySettingsItems, loadingKeys } = useSelector((state) => state.productCatalog);
  const {
    forecastDemandPlanningList,
    loadingKeys: demandPlanningLoadingKeys,
    forecastDemandPlanningListLoaded,
    forecastDemandPlanningListFetchFailed,
  } = useSelector((state) => state.demandPlanning);
  const { currentUser } = useSelector((state) => state.auth);
  const { childIdToAccess: childPermissionAccess } = getAllowedUserPermissions({
    permissions: currentUser?.permissions || [],
    isAdmin: isAdminRole(currentUser?.role),
  });
  const sheetImportAccess = childPermissionAccess.get(CHILDREN_TAB_DEMAND_PLAN_SHEET_IMPORT);

  const [columnDrawerOpen, setColumnDrawerOpen] = useState(false);
  const [governanceDialogOpen, setGovernanceDialogOpen] = useState(false);
  const [governanceViewId, setGovernanceViewId] = useState(null);
  const [demandPlanningColumnByView, setDemandPlanningColumnByView] = useState({});
  const [columnDrawerDraft, setColumnDrawerDraft] = useState([]);
  const [ddpMobileVisibleColumnIds, setDdpMobileVisibleColumnIds] = useState(['supplySku', 'rangeShare']);
  const [selectedForecastDemandPlanning, setSelectedForecastDemandPlanning] = useState(null);
  const [sheetImportOpen, setSheetImportOpen] = useState(false);
  const [forecastSidebarOpen, setForecastSidebarOpen] = useState(false);
  const [forecastSidebarMode, setForecastSidebarMode] = useState('create');
  const [forecastSidebarRecord, setForecastSidebarRecord] = useState(null);
  const [forecastDeleteConfirmId, setForecastDeleteConfirmId] = useState(null);
  const [undoRestoreModalOpen, setUndoRestoreModalOpen] = useState(false);
  const [overrideResetEpoch, setOverrideResetEpoch] = useState(0);
  const [historyDrawerOpen, setHistoryDrawerOpen] = useState(false);
  const [activeView, setActiveView] = useState(null);
  const [listFetchRetry, setListFetchRetry] = useState({ id: null, retries: 0 });
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get('search') || '');
  const [selectedCategoryId, setSelectedCategoryId] = useState(() =>
    parseCommaSeparatedParam(searchParams.get('categories'))
  );
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState(() =>
    parseCommaSeparatedParam(searchParams.get('subcategories'))
  );
  const [selectedStatusId, setSelectedStatusId] = useState(() =>
    parseCommaSeparatedParam(searchParams.get('statuses'))
  );
  const forecastDemandRefreshYearRef = useRef(null);
  const [effectiveForecastYear, setEffectiveForecastYear] = useState(null);
  const [salesSnapshotVersion, setSalesSnapshotVersion] = useState(0);
  const [drawerWidth, setDrawerWidth] = useState(
    `${window.innerWidth - Number(marginFromSidebar)}px`
  );

  useEffect(() => {
    const handleResize = () => {
      setDrawerWidth(`${window.innerWidth - Number(marginFromSidebar)}px`);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [marginFromSidebar]);

  const MIN_ALLOWED_DATE = "2024-01-01";
  const pad2 = (n) => String(n).padStart(2, '0');
  const todayYmd = () => {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}-${pad2(now.getDate())}`;
  };
  const clampToAllowedRange = (ymd) => {
    if (!ymd) return ymd;
    if (ymd < MIN_ALLOWED_DATE) return MIN_ALLOWED_DATE;
    const today = todayYmd();
    return ymd > today ? today : ymd;
  };

  const getCurrentMonthDistributionProfileRange = () => {
    const now = new Date();
    const y = now.getFullYear();
    const m = now.getMonth();
    const startRaw = `${y}-${pad2(m + 1)}-01`;
    return {
      start: clampToAllowedRange(startRaw),
      end: todayYmd(),
    };
  };

  const [startDistributionProfileDate, setStartDistributionProfileDate] = useState(() =>
    getCurrentMonthDistributionProfileRange().start
  );
  const [endDistributionProfileDate, setEndDistributionProfileDate] = useState(() =>
    getCurrentMonthDistributionProfileRange().end
  );
  const handleStartDateChange = (nextStart) => {
    const clamped = clampToAllowedRange(nextStart);
    setStartDistributionProfileDate(clamped);
    if (endDistributionProfileDate && clamped && clamped > endDistributionProfileDate) {
      setEndDistributionProfileDate(clamped);
    }
  };
  const handleDistributionProfileRangeChange = ({ start, end }) => {
    setStartDistributionProfileDate(clampToAllowedRange(start));
    setEndDistributionProfileDate(clampToAllowedRange(end));
  };
  const handleEndDateChange = (nextEnd) => {
    const clamped = clampToAllowedRange(nextEnd);
    setEndDistributionProfileDate(clamped);
    if (startDistributionProfileDate && clamped && clamped < startDistributionProfileDate) {
      setStartDistributionProfileDate(clamped);
    }
  };

  const isCategorySettingsLoading = loadingKeys.includes(
    ProductCatalogLoadingKeys.fetchCategorySettings
  );

  const categoryFilterOptions = useMemo(
    () =>
      (categorySettingsItems || []).map((group) => ({
        label: group.name,
        value: group.id,
      })),
    [categorySettingsItems]
  );

  const subcategoryFilterOptions = useMemo(() => {
    const map = new Map();
    (categorySettingsItems || []).forEach((group) => {
      (group.variants || []).forEach((variant) => {
        if (!map.has(variant.id)) {
          map.set(variant.id, {
            label: `${group.name} - ${variant.name}`,
            value: variant.id,
          });
        }
      });
    });
    return Array.from(map.values()).sort((a, b) =>
      a.label.localeCompare(b.label)
    );
  }, [categorySettingsItems]);

  useEffect(() => {
    dispatch(fetchProductCatalogCategorySettings());
  }, [dispatch]);

  useEffect(() => {
    if (!selectedForecastDemandPlanning?.id) {
      setEffectiveForecastYear(null);
      forecastDemandRefreshYearRef.current = null;
      return;
    }
    const fromRow =
      selectedForecastDemandPlanning?.forecastYear ??
      selectedForecastDemandPlanning?.forecast_year;
    if (fromRow != null && fromRow !== '') {
      const yearStr = String(fromRow);
      setEffectiveForecastYear(yearStr);
      forecastDemandRefreshYearRef.current = yearStr;
      return;
    }
    setEffectiveForecastYear(null);
    forecastDemandRefreshYearRef.current = null;
  }, [
    selectedForecastDemandPlanning?.id,
    selectedForecastDemandPlanning?.forecastYear,
    selectedForecastDemandPlanning?.forecast_year,
  ]);

  useEffect(() => {
    if (!selectedForecastDemandPlanning?.id) {
      dispatch(fetchForecastDemandPlanningList());
    }
  }, [dispatch, selectedForecastDemandPlanning]);

  useEffect(() => {
    if (!idFromUrl || !forecastDemandPlanningListFetchFailed) {
      listFetchRetryInFlightRef.current = null;
      return;
    }

    const idKey = String(idFromUrl);
    if (listFetchRetryInFlightRef.current === idKey) return;
    const currentRetry = listFetchRetry.id === idKey ? listFetchRetry : { id: idKey, retries: 0 };
    if (currentRetry.retries >= 2) return;

    listFetchRetryInFlightRef.current = idKey;
    setListFetchRetry({ id: idKey, retries: currentRetry.retries + 1 });
    dispatch(fetchForecastDemandPlanningList());
  }, [dispatch, forecastDemandPlanningListFetchFailed, idFromUrl, listFetchRetry]);

  /** Drop cached table/API state when switching between forecast demand plans. */
  useEffect(() => {
    if (!selectedForecastDemandPlanning?.id) return;
    dispatch(clearDemandPlanningTableData());
    dispatch(setForecastDemandPlanningData(null));
    setSalesSnapshotVersion(0);
  }, [dispatch, selectedForecastDemandPlanning?.id]);

  /** When list updates, merge metadata into the selected row. List payload omits `data` (large JSON); keep existing `data` unless list supplies it. */
  useEffect(() => {
    if (!selectedForecastDemandPlanning?.id) return;
    if (!forecastDemandPlanningListLoaded) return;
    const selectedId = String(selectedForecastDemandPlanning.id);
    const match = forecastDemandPlanningList.find((r) => String(r.id) === selectedId);
    if (!match) return;
    setSelectedForecastDemandPlanning((prev) => {
      if (!prev || String(prev.id) !== selectedId) return prev;
      return selectForecastDemandPlanningRow(match, { preserveDataFrom: prev });
    });
  }, [forecastDemandPlanningList, selectedForecastDemandPlanning?.id]);

  /** Open the forecast from `?id=` after the list loads (e.g. full page reload). */
  useEffect(() => {
    if (!idFromUrl) {
      urlHydratedForIdRef.current = null;
      /** Sidebar/menu links use `/demand-planning` without `id`; drop stale selection so the list shows. */
      setSelectedForecastDemandPlanning((prev) => (prev ? null : prev));
      setActiveView((v) => (v ? null : v));
      setSearchTerm('');
      setSelectedCategoryId([]);
      setSelectedSubcategoryId([]);
      setSelectedStatusId([]);
      return;
    }
    if (!Array.isArray(forecastDemandPlanningList) || !forecastDemandPlanningListLoaded) return;
    const match = forecastDemandPlanningList.find((r) => String(r.id) === String(idFromUrl));
    if (!match) {
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.delete('id');
          next.delete('view');
          clearFilterParamsFromSearchParams(next);
          return next;
        },
        { replace: true }
      );
      urlHydratedForIdRef.current = null;
      setSearchTerm('');
      setSelectedCategoryId([]);
      setSelectedSubcategoryId([]);
      setSelectedStatusId([]);
      return;
    }
    const { nextViewId } = getViewPermissionState(viewFromUrl);
    if (!nextViewId) {
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.delete('id');
          next.delete('view');
          clearFilterParamsFromSearchParams(next);
          return next;
        },
        { replace: true }
      );
      setSelectedForecastDemandPlanning(null);
      setActiveView(null);
      urlHydratedForIdRef.current = null;
      return;
    }
    const urlIdKey = String(idFromUrl);
    const alreadyHydrated =
      urlHydratedForIdRef.current === urlIdKey &&
      selectedForecastDemandPlanning?.id != null &&
      String(selectedForecastDemandPlanning.id) === urlIdKey;
    if (alreadyHydrated) return;
    urlHydratedForIdRef.current = urlIdKey;
    setSelectedForecastDemandPlanning((prev) =>
      selectForecastDemandPlanningRow(match, {
        preserveDataFrom: prev && String(prev.id) === urlIdKey ? prev : null,
      })
    );
    setActiveView(DEMAND_PLANNING_VIEW_PRESETS.find((p) => p.id === nextViewId));
    if (viewFromUrl !== nextViewId) {
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.set('view', nextViewId);
          return next;
        },
        { replace: true }
      );
    }
  }, [
    idFromUrl,
    viewFromUrl,
    forecastDemandPlanningList,
    forecastDemandPlanningListLoaded,
    setSearchParams,
    selectedForecastDemandPlanning?.id,
    allowedPermissionIds,
    getViewPermissionState,
  ]);

  /** Keep the in-app view in sync when only `view` changes in the URL (sidebar links). */
  useEffect(() => {
    if (!idFromUrl) return;

    const { nextViewId } = getViewPermissionState(viewFromUrl);
    if (!nextViewId) {
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.delete('id');
          next.delete('view');
          clearFilterParamsFromSearchParams(next);
          return next;
        },
        { replace: true }
      );
      return;
    }
    if (viewFromUrl !== nextViewId) {
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.set('view', nextViewId);
          return next;
        },
        { replace: true }
      );
      return;
    }

    setActiveView((prev) => {
      if (prev?.id === nextViewId) return prev;
      return DEMAND_PLANNING_VIEW_PRESETS.find((p) => p.id === nextViewId) || prev;
    });
  }, [idFromUrl, viewFromUrl, allowedPermissionIds, setSearchParams, getViewPermissionState]);

  const deleteForecastPlanningIsLoading =
    demandPlanningLoadingKeys.includes(DemandPlanningLoadingKeys.deleteForecastDemandPlanning);
  const forecastSidebarSaving =
    demandPlanningLoadingKeys.includes(DemandPlanningLoadingKeys.createForecastDemandPlanning) ||
    demandPlanningLoadingKeys.includes(DemandPlanningLoadingKeys.updateForecastDemandPlanning);

  const handleForecastSidebarSubmit = useCallback(
    async (payload) => {
      try {
        if (forecastSidebarMode === 'create') {
          const res = await dispatch(createForecastDemandPlanning(payload)).unwrap();
          try {
            await dispatch(fetchForecastDemandPlanningList()).unwrap();
          } catch {
            /* list may still be current from create fulfilled */
          }
          const id = res?.item?.id;
          if (id != null) {
            const list = store.getState().demandPlanning.forecastDemandPlanningList || [];
            const fresh = list.find((r) => String(r.id) === String(id)) ?? res.item;
            setSelectedForecastDemandPlanning(fresh);
            setActiveView(DEMAND_PLANNING_VIEW_PRESETS.find((p) => p.id === 'forecast-demand'));
            setSearchParams(
              (sp) => {
                const next = new URLSearchParams(sp);
                next.set('id', String(id));
                next.set('view', 'forecast-demand');
                return next;
              },
              { replace: true }
            );
            urlHydratedForIdRef.current = String(id);
          }
        } else if (forecastSidebarRecord?.id) {
          const res = await dispatch(
            updateForecastDemandPlanning({ id: forecastSidebarRecord.id, ...payload })
          ).unwrap();
          try {
            await dispatch(fetchForecastDemandPlanningList()).unwrap();
          } catch {
            /* ignore */
          }
          const updatedId = forecastSidebarRecord.id;
          const list = store.getState().demandPlanning.forecastDemandPlanningList || [];
          const fresh = list.find((r) => String(r.id) === String(updatedId)) ?? res?.item;
          if (
            fresh &&
            selectedForecastDemandPlanning?.id != null &&
            String(selectedForecastDemandPlanning.id) === String(updatedId)
          ) {
            setSelectedForecastDemandPlanning(fresh);
          }
        }
        setForecastSidebarOpen(false);
        setForecastSidebarRecord(null);
      } catch (error){
        console.error('Failed to save demand planning.', error);
      }
    },
    [dispatch, forecastSidebarMode, forecastSidebarRecord, selectedForecastDemandPlanning?.id, setSearchParams]
  );

  const handleConfirmDeleteForecast = useCallback(async () => {
    if (forecastDeleteConfirmId == null) return;
    const deletedId = forecastDeleteConfirmId;
    try {
      await dispatch(deleteForecastDemandPlanning(deletedId)).unwrap();
      setForecastDeleteConfirmId(null);
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          if (String(next.get('id')) === String(deletedId)) next.delete('id');
          return next;
        },
        { replace: true }
      );
      if (String(selectedForecastDemandPlanning?.id) === String(deletedId)) {
        setSelectedForecastDemandPlanning(null);
        setActiveView(null);
        urlHydratedForIdRef.current = null;
      }
    } catch (error){
      console.error('Failed to delete demand planning.', error);
    }
  }, [dispatch, forecastDeleteConfirmId, setSearchParams, selectedForecastDemandPlanning?.id]);

  const handleConvertToTrueGross = useCallback(async () => {
    const plan = selectedForecastDemandPlanning;
    const budgetId = plan?.budgetId || plan?.connectedBudgetId;
    if (!plan?.id || !budgetId) return;
    const previousTarget = plan.revenueTarget;
    if (!window.confirm(`Convert this plan to true gross sales?\nPrevious target: ${formatForecastRevenueDisplay(previousTarget)}`)) {
      return;
    }
    try {
      const response = await axios.post(`/api/v1/forecast-demand-planning/${plan.id}/convert-to-true-gross`, {
        budgetId,
      });
      setSelectedForecastDemandPlanning((current) => ({
        ...current,
        revenueTarget: response.data?.new_target,
        gross_basis: 'true_gross',
        basis_conversion: response.data,
      }));
      toast.success('Demand Plan converted to true gross sales.');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not convert Demand Plan to true gross sales.');
    }
  }, [selectedForecastDemandPlanning]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get('/api/v1/metadata/demand-planning-columns');
        const byView = data?.demand_planning_columns?.by_view;
        if (!cancelled && byView && typeof byView === 'object' && !Array.isArray(byView)) {
          setDemandPlanningColumnByView(byView);
        }
      } catch {
        toast.error('Could not load demand planning column settings.', { position: 'bottom-right' });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const presetColumnIdsForActiveView = useMemo(() => {
    const ids =
      activeView?.columnIds?.length > 0
        ? activeView.columnIds
        : PRESET_COLUMN_IDS[activeView?.id] || [];
    return dedupeDemandPlanningColumnIds(ids);
  }, [activeView]);

  const demandPlanningYear =
    selectedForecastDemandPlanning?.forecast_year ??
    selectedForecastDemandPlanning?.forecastYear ??
    null;

  const resolvedVisibleColumnIds = useMemo(
    () =>
      getVisibleDemandPlanningColumnIds(
        presetColumnIdsForActiveView,
        demandPlanningColumnByView[activeView?.id],
        activeView?.id,
        demandPlanningYear
      ),
    [
      presetColumnIdsForActiveView,
      demandPlanningColumnByView,
      activeView?.id,
      demandPlanningYear,
    ]
  );

  const activePresetColumnLayoutCustomized = useMemo(
    () =>
      isDemandPlanningColumnLayoutCustomized(
        activeView?.id,
        presetColumnIdsForActiveView,
        demandPlanningColumnByView[activeView?.id],
        demandPlanningYear
      ),
    [activeView?.id, demandPlanningColumnByView, presetColumnIdsForActiveView, demandPlanningYear]
  );

  const hydrateColumnDrawerDraft = useCallback(() => {
    setColumnDrawerDraft(
      buildDemandPlanningColumnDrawerRows(
        presetColumnIdsForActiveView,
        activeView?.id,
        demandPlanningColumnByView[activeView?.id],
        demandPlanningYear
      )
    );
  }, [activeView?.id, presetColumnIdsForActiveView, demandPlanningColumnByView, demandPlanningYear]);

  useEffect(() => {
    if (!columnDrawerOpen) return;
    hydrateColumnDrawerDraft();
  }, [columnDrawerOpen, hydrateColumnDrawerDraft]);

  const handleSaveDemandPlanningColumns = useCallback(async () => {
    const visibility = {};
    columnDrawerDraft.forEach((c) => {
      visibility[c.id] = c.visible;
    });
    const nextByView = {
      ...demandPlanningColumnByView,
      [activeView.id]: { visibility },
    };
    try {
      await axios.post('/api/v1/metadata/demand-planning-columns', {
        demand_planning_columns: { by_view: nextByView },
      });
      setDemandPlanningColumnByView(nextByView);
      toast.success('Column visibility saved.', { position: 'bottom-right' });
      setColumnDrawerOpen(false);
    } catch {
      toast.error('Could not save column visibility.', { position: 'bottom-right' });
    }
  }, [columnDrawerDraft, demandPlanningColumnByView, activeView]);

  const openColumnsDrawer = useCallback(() => {
    hydrateColumnDrawerDraft();
    setColumnDrawerOpen(true);
  }, [hydrateColumnDrawerDraft]);

  const clearFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedCategoryId([]);
    setSelectedSubcategoryId([]);
    setSelectedStatusId([]);
  }, []);

  useEffect(() => {
    if (!selectedForecastDemandPlanning?.id) return;

    const urlSearch = searchParams.get('search') || '';
    const urlCategories = parseCommaSeparatedParam(searchParams.get('categories'));
    const urlSubcategories = parseCommaSeparatedParam(searchParams.get('subcategories'));
    const urlStatuses = parseCommaSeparatedParam(searchParams.get('statuses'));

    setSearchTerm((prev) => (prev === urlSearch ? prev : urlSearch));
    setSelectedCategoryId((prev) =>
      filterArraysEqual(prev, urlCategories) ? prev : urlCategories
    );
    setSelectedSubcategoryId((prev) =>
      filterArraysEqual(prev, urlSubcategories) ? prev : urlSubcategories
    );
    setSelectedStatusId((prev) =>
      filterArraysEqual(prev, urlStatuses) ? prev : urlStatuses
    );
  }, [searchParams, selectedForecastDemandPlanning?.id]);

  useEffect(() => {
    if (!selectedForecastDemandPlanning?.id) return;

    const nextSearch = searchTerm || '';
    const nextCategories = selectedCategoryId.join(',');
    const nextSubcategories = selectedSubcategoryId.join(',');
    const nextStatuses = selectedStatusId.join(',');

    const currentSearch = searchParams.get('search') || '';
    const currentCategories = searchParams.get('categories') || '';
    const currentSubcategories = searchParams.get('subcategories') || '';
    const currentStatuses = searchParams.get('statuses') || '';

    if (
      nextSearch === currentSearch &&
      nextCategories === currentCategories &&
      nextSubcategories === currentSubcategories &&
      nextStatuses === currentStatuses
    ) {
      return;
    }

    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        applyFilterParamsToSearchParams(next, {
          searchTerm,
          selectedCategoryId,
          selectedSubcategoryId,
          selectedStatusId,
        });
        return next;
      },
      { replace: true }
    );
  }, [
    searchTerm,
    selectedCategoryId,
    selectedSubcategoryId,
    selectedStatusId,
    selectedForecastDemandPlanning?.id,
    searchParams,
    setSearchParams,
  ]);

  const onSelectView = useCallback((viewId) => {
    setActiveView(DEMAND_PLANNING_VIEW_PRESETS.find(preset => preset.id === viewId));
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        next.set('view', viewId);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const governancePreset = useMemo(
    () => DEMAND_PLANNING_VIEW_PRESETS.find(preset => preset.id === (governanceViewId ?? activeView?.id)),
    [governanceViewId, activeView?.id],
  );

  const onOpenGovernanceDialog = useCallback((viewId) => {
    setGovernanceViewId(viewId);
    setGovernanceDialogOpen(true);
  }, []);

  const demandListIsLoading = demandPlanningLoadingKeys.includes(DemandPlanningLoadingKeys.fetchForecastDemandPlanningList);
  const selectedPlanMatchesUrl =
    selectedForecastDemandPlanning?.id != null &&
    idFromUrl != null &&
    String(selectedForecastDemandPlanning.id) === String(idFromUrl);
  useEffect(() => {
    if (!sheetImportFromUrl || !selectedPlanMatchesUrl) return;
    if (sheetImportAccess) setSheetImportOpen(true);
    setSearchParams(
      (sp) => {
        const next = new URLSearchParams(sp);
        next.delete('import');
        return next;
      },
      { replace: true }
    );
  }, [sheetImportFromUrl, selectedPlanMatchesUrl, sheetImportAccess, setSearchParams]);
  const deepLinkRetryExhausted =
    Boolean(idFromUrl) &&
    forecastDemandPlanningListFetchFailed &&
    listFetchRetry.id === String(idFromUrl) &&
    listFetchRetry.retries >= 2;
  const isDeepLinkHydrating =
    Boolean(idFromUrl) &&
    !selectedPlanMatchesUrl;
  const isRefreshingSalesSnapshot = demandPlanningLoadingKeys.includes(
    DemandPlanningLoadingKeys.refreshForecastDemandSalesSnapshot
  );

  const handleRefreshSalesSnapshot = useCallback(async () => {
    const planningId = selectedForecastDemandPlanning?.id;
    if (!planningId) {
      const msg = 'No forecast demand plan selected';
      toast.error(msg, { position: 'bottom-right' });
      throw new Error(msg);
    }
    const forecastYear =
      forecastDemandRefreshYearRef.current ||
      selectedForecastDemandPlanning?.forecastYear ||
      selectedForecastDemandPlanning?.forecast_year ||
      null;
    try {
      const result = await dispatch(
        refreshForecastDemandSalesSnapshot({ id: planningId, forecastYear })
      ).unwrap();
      if (result?.item) {
        setSelectedForecastDemandPlanning(result.item);
      }
      setSalesSnapshotVersion((version) => version + 1);
      const stats = result?.productStats;
      const added = Number(stats?.added || 0);
      const updated = Number(stats?.updated || 0);
      const removed = Number(stats?.removed || 0);
      const actualYear =
        stats?.actualYear != null
          ? Number(stats.actualYear)
          : forecastYear != null
            ? Number(forecastYear) - 1
            : null;
      const parts = [`Updated ${updated} product(s)`];
      if (added > 0) parts.push(`added ${added} new product(s)`);
      if (removed > 0) {
        const scopeHint =
          Number.isFinite(actualYear)
            ? `excluded from forecast before ${actualYear} or missing category/subcategory`
            : 'outside forecast catalog scope';
        parts.push(`removed ${removed} product(s) (${scopeHint})`);
      }
      toast.success(`Sales snapshot refreshed. ${parts.join(', ')}.`, { position: 'bottom-right' });
    } catch (err) {
      const msg =
        typeof err === 'string'
          ? err
          : err?.message || 'Failed to refresh sales snapshot';
      toast.error(msg, { position: 'bottom-right' });
      throw err;
    }
  }, [dispatch, selectedForecastDemandPlanning]);

  const handleDemandPlanSheetImportApplied = async () => {
    await dispatch(fetchForecastDemandPlanningList()).unwrap();
    const fresh = (store.getState().demandPlanning.forecastDemandPlanningList || [])
      .find((row) => String(row.id) === String(selectedForecastDemandPlanning?.id));
    if (fresh) setSelectedForecastDemandPlanning(fresh);
  };

  const isDefaultTable = useMemo(
    () => isDefaultPlanningTableView(activeView?.id),
    [activeView?.id]
  );
  const isForecastDemandTable = useMemo(() => activeView?.id === 'forecast-demand', [activeView?.id]);
  const isApprovedBuysTable = useMemo(() => activeView?.id === 'approved-buys', [activeView?.id]);
  const isAiRebalanceTable = useMemo(() => activeView?.id === 'ai-rebalance', [activeView?.id]);
  const isDemandDistributionProfileTable = useMemo(() => activeView?.id === 'demand-distribution-profile', [activeView?.id]);
  const isCashLiabilitiesTable = useMemo(() => activeView?.id === 'cash-liabilities', [activeView?.id]);
  const isUpcomingBuysTable = useMemo(() => activeView?.id === 'upcoming-buys', [activeView?.id]);
  const isPoScheduleTable = useMemo(() => activeView?.id === 'po-schedule', [activeView?.id]);
  const isBundleDemandExplosionTable = useMemo(() => activeView?.id === 'bundle-demand-explosion', [activeView?.id]);
  const isBundleReplacementShiftTable = useMemo(() => activeView?.id === 'bundle-replacement-shift', [activeView?.id]);
  const isReplacementDemandShiftTable = useMemo(() => activeView?.id === 'replacement-demand-shift', [activeView?.id]);
  const isPurchaseRoundingTable = useMemo(() => activeView?.id === 'purchase-rounding', [activeView?.id]);
  const isContainerOptimizationTable = useMemo(() => activeView?.id === 'container-optimization', [activeView?.id]);
  const isInventoryCapitalPlanTable = useMemo(() => activeView?.id === 'inventory-capital-plan', [activeView?.id]);
  const isComponentDemandLedgerTable = useMemo(() => activeView?.id === 'component-demand-ledger', [activeView?.id]);

  const ddpMobileColumnOptions = useMemo(
    () =>
      (PRESET_COLUMN_IDS['demand-distribution-profile'] || []).map((id) => ({
        id,
        label: id === 'productName' ? 'Product' : getDemandPlanningColumnLabel(id),
      })),
    []
  );

  const handleToggleDdpMobileColumn = useCallback((columnId) => {
    setDdpMobileVisibleColumnIds((current) => {
      const baseOrder = PRESET_COLUMN_IDS['demand-distribution-profile'] || [];
      const next = current.includes(columnId)
        ? current.filter((id) => id !== columnId)
        : baseOrder.filter((id) => current.includes(id) || id === columnId);
      return next.length === 0 ? current : next;
    });
  }, []);

  const showFilters = !isForecastDemandTable;
  const showAdditionalButtons = 
    !isForecastDemandTable && 
    !isAiRebalanceTable && 
    !isBundleDemandExplosionTable && 
    !isBundleReplacementShiftTable && 
    !isReplacementDemandShiftTable &&
    !isPurchaseRoundingTable &&
    !isContainerOptimizationTable &&
    !isInventoryCapitalPlanTable &&
    !isComponentDemandLedgerTable;
  const showUndoRestoreButton = useMemo(() => 
    activeView?.id === 'sku-allocation' || 
    activeView?.id === 'eligibility-validation' || 
    activeView?.id === 'forecast-by-sku' || 
    activeView?.id === 'open-to-buy',
    [activeView?.id]
  );
  const showChangeHistoryButton = showUndoRestoreButton;

  const refetchAfterUndoRestore = useCallback(async () => {
    const planId = selectedForecastDemandPlanning?.id;
    const planYear =
      selectedForecastDemandPlanning?.forecast_year ??
      selectedForecastDemandPlanning?.forecastYear;
    if (planId == null || planYear === undefined || planYear === null || planYear === '') {
      return;
    }

    const refetchParams = {
      demandPlanningId: planId,
      demandPlanningYear: planYear,
      searchTerm,
      selectedCategoryId,
      selectedSubcategoryId,
      selectedStatusId,
    };

    if (activeView?.id === 'forecast-by-sku') {
      await dispatch(getForecastBySkuTableData(refetchParams)).unwrap();
    } else if (activeView?.id === 'open-to-buy') {
      await dispatch(getOpenToBuyTableData(refetchParams)).unwrap();
    } else if (activeView?.id === 'eligibility-validation') {
      await dispatch(getEligibilityValidationTableData(refetchParams)).unwrap();
    } else {
      await dispatch(getSkuAllocationTableData(refetchParams)).unwrap();
    }

    setOverrideResetEpoch((current) => current + 1);
  }, [
    activeView?.id,
    dispatch,
    searchTerm,
    selectedCategoryId,
    selectedForecastDemandPlanning,
    selectedStatusId,
    selectedSubcategoryId,
  ]);

  const onOpenUndoRestore = useCallback(() => {
    setUndoRestoreModalOpen(true);
  }, []);

  const openForecastPlanningRow = useCallback(
    (record) => {
      const nextId = String(record.id);
      setSelectedForecastDemandPlanning(
        selectForecastDemandPlanningRow(record)
      );
      setActiveView(DEMAND_PLANNING_VIEW_PRESETS.find(preset => preset.id === 'forecast-demand'));
      setSearchParams(
        (sp) => {
          const next = new URLSearchParams(sp);
          next.set('id', nextId);
          next.set('view', 'forecast-demand');
          return next;
        },
        { replace: true }
      );
      urlHydratedForIdRef.current = nextId;
    },
    [setSearchParams]
  );

  return (
    <>
      {isDeepLinkHydrating && !deepLinkRetryExhausted ? (
        <div className="flex justify-center items-center h-full py-8">
          <LoaderSpinner width="80" height="80" border="3" colorSpinner="black"/>
        </div>
      ) : isDeepLinkHydrating && deepLinkRetryExhausted ? (
        <div className="w-full px-4 py-6 sm:px-6 lg:px-8 mb-10">
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            <p>Could not load the demand planning list.</p>
            <button
              type="button"
              className="mt-2 font-medium underline hover:no-underline"
              onClick={() => {
                listFetchRetryInFlightRef.current = null;
                setListFetchRetry({ id: String(idFromUrl), retries: 0 });
                dispatch(fetchForecastDemandPlanningList());
              }}
            >
              Retry
            </button>
          </div>
        </div>
      ) : !selectedForecastDemandPlanning ? (
        <main className="w-full h-auto px-4 py-6 sm:px-6 lg:px-8 mb-10">
          <div className="h-auto flex flex-col">
            <section className="flex flex-wrap items-center justify-between gap-2 mb-4">
              <div className="flex min-w-0 shrink-0 items-baseline gap-2">
                <h2 className="whitespace-nowrap text-[14px] font-bold leading-none tracking-tight text-gray-900 sm:text-[20px]">
                  Demand Planning
                </h2>
                {!demandListIsLoading && (
                  <p className="hidden whitespace-nowrap text-[13px] tabular-nums text-gray-400 sm:inline">
                    {forecastDemandPlanningList?.length ?? 0} {(forecastDemandPlanningList?.length ?? 0) === 1 ? 'plan' : 'plans'}
                  </p>
                )}
              </div>

              {access === "edit" && (
                <button
                  type="button"
                  disabled={demandListIsLoading}
                  className="inline-flex items-center gap-2 rounded-md border border-black bg-black px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50"
                  onClick={() => {
                    setForecastSidebarRecord(null);
                    setForecastSidebarMode('create');
                    setForecastSidebarOpen(true);
                  }}
                >
                  <PlusIcon className="h-4 w-4" /> Create demand planning
                </button>
              )}
            </section>
          </div>
          {demandListIsLoading ? (
            <div className="flex justify-center items-center h-full py-8">
              <LoaderSpinner width="80" height="80" border="3" colorSpinner="black"/>
            </div>
          ) : (
            <div className="w-full flex flex-col gap-4">
              <section className="flex flex-col gap-2">
                <div className="relative">
                  <div className="w-full">
                    <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
                      <TanStackTable
                        compact
                        data={forecastDemandPlanningList}
                        hasActions
                        onRowClick={(row) => openForecastPlanningRow(row.original)}
                        columns={[
                          {
                            accessorKey: "name",
                            header: "Name",
                            enableSorting: false,
                            headerTrClassName: "min-w-[250px]",
                            cell: ({ getValue }) => (
                              <span className="font-medium text-gray-900">{getValue()}</span>
                            ),
                          },
                          {
                            accessorKey: "forecastYear",
                            header: "Forecast year",
                            enableSorting: false,
                            headerTrClassName: "min-w-[150px]",
                            cell: ({ getValue }) => (
                              <span className="tabular-nums">{getValue()}</span>
                            ),
                          },
                          {
                            accessorKey: "revenueTarget",
                            header: "Revenue Target (True Gross)",
                            enableSorting: false,
                            headerTrClassName: "min-w-[200px]",
                            headerClassName: "justify-end",
                            cell: ({ getValue }) => (
                              <span className="block text-right font-medium tabular-nums text-gray-900">
                                {formatForecastRevenueDisplay(getValue())}
                              </span>
                            ),
                          },
                          {
                            id: "workbookBasis",
                            header: "Workbook basis",
                            enableSorting: false,
                            headerTrClassName: "min-w-[170px]",
                            headerClassName: "justify-end",
                            cell: ({ row }) => (
                              <span className="block text-right tabular-nums text-gray-500">
                                {row.original.basis_conversion?.previous_target != null ? formatForecastRevenueDisplay(row.original.basis_conversion.previous_target) : "—"}
                              </span>
                            ),
                          },
                          {
                            id: "grossBasis",
                            header: "Basis",
                            enableSorting: false,
                            headerTrClassName: "min-w-[130px]",
                            cell: ({ row }) => (
                              <span className="text-gray-600">
                                {row.original.gross_basis === 'true_gross' ? 'True Gross Sales' : 'Workbook'}
                              </span>
                            ),
                          },
                          {
                            accessorKey: "publishStatus",
                            header: "Status",
                            enableSorting: false,
                            headerTrClassName: "min-w-[130px]",
                            cell: ({ row }) => {
                              const isPublished = row.original.publishStatus === 'PUBLISHED';
                              return (
                                <span
                                  className={`inline-flex items-center gap-1.5 rounded-sm px-2 py-0.5 text-[11px] font-medium leading-none tracking-wide ${
                                    isPublished
                                      ? 'bg-emerald-50 text-emerald-800'
                                      : 'bg-gray-100 text-gray-500'
                                  }`}
                                >
                                  <span
                                    className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                                      isPublished ? 'bg-emerald-500' : 'bg-gray-400'
                                    }`}
                                    aria-hidden
                                  />
                                  {isPublished ? 'Published' : 'Draft'}
                                </span>
                              );
                            },
                          },
                          {
                            accessorKey: "notes",
                            header: "Notes",
                            enableSorting: false,
                            headerTrClassName: "min-w-[260px]",
                            cell: ({ getValue }) => getValue() || "—",
                          },
                          {
                            accessorKey: "userName",
                            header: "User",
                            enableSorting: false,
                            headerTrClassName: "min-w-[180px]",
                          },
                          {
                            accessorKey: "createdAtStr",
                            header: "Created",
                            enableSorting: false,
                            headerTrClassName: "min-w-[260px]",
                            cell: ({ row }) => (
                              <span className="whitespace-nowrap">
                                {formatForecastDemandPlanningCreatedDisplay(row.original)}
                              </span>
                            ),
                          },
                          access === "edit"
                            ? {
                                accessorKey: "actions",
                                header: "Actions",
                                enableSorting: false,
                                headerTrClassName: "min-w-[120px]",
                                headerClassName: "justify-end",
                                cell: ({ row }) => {
                                  const isLoading =
                                    deleteForecastPlanningIsLoading &&
                                    forecastDeleteConfirmId === row.original.id;
                                  return (
                                    <div className="inline-flex items-center gap-1 justify-end w-full">
                                      <button
                                        type="button"
                                        title="Open plan"
                                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          openForecastPlanningRow(row.original);
                                        }}
                                      >
                                        <EyeIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        title="Edit plan"
                                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setForecastSidebarRecord(row.original);
                                          setForecastSidebarMode('edit');
                                          setForecastSidebarOpen(true);
                                        }}
                                      >
                                        <PencilIcon className="h-4 w-4" />
                                      </button>
                                      <button
                                        type="button"
                                        title="Delete plan"
                                        disabled={isLoading}
                                        className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-red-600 disabled:opacity-50"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setForecastDeleteConfirmId(row.original.id);
                                        }}
                                      >
                                        {isLoading ? (
                                          <ArrowPathIcon className="animate-spin h-4 w-4" />
                                        ) : (
                                          <TrashIcon
                                            className="h-4 w-4"
                                            aria-hidden="true"
                                          />
                                        )}
                                      </button>
                                    </div>
                                  );
                                },
                              }
                            : null,
                        ].filter(Boolean)}
                      />
                      {access === "edit" && (
                        <div className="border-t border-gray-200 px-4 py-2.5 text-xs text-gray-500">
                          Actions: <span className="text-gray-700">Open</span> loads the plan · <span className="text-gray-700">Edit</span> opens the plan drawer · <span className="text-gray-700">Delete</span> removes the plan after confirmation.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </section>
            </div>
          )}

          <ForecastDemandPlanningSidebar
            isOpen={forecastSidebarOpen}
            onClose={() => {
              setForecastSidebarOpen(false);
              setForecastSidebarRecord(null);
            }}
            mode={forecastSidebarMode}
            record={forecastSidebarRecord}
            isSaving={forecastSidebarSaving}
            onSubmit={handleForecastSidebarSubmit}
          />

          <ConfrimModal
            isOpen={forecastDeleteConfirmId != null}
            isLoading={deleteForecastPlanningIsLoading}
            title="Delete this demand planning record? This cannot be undone."
            confirmButtonTitle="Delete"
            cancelClick={() => setForecastDeleteConfirmId(null)}
            submitClick={handleConfirmDeleteForecast}
            size="max-w-lg"
          />
        </main>
      ) : (
        <main className={`w-full h-auto px-4 sm:px-6 lg:px-8 ${isDemandDistributionProfileTable ? 'py-3 md:py-6' : 'py-6'}`}>
          <section className={`flex flex-col h-auto xl:h-[55px] lg:flex-row lg:items-center lg:justify-between ${isDemandDistributionProfileTable ? 'gap-0 pb-2 md:gap-3 md:pb-4' : 'gap-3 pb-4'} ${showFilters ? 'border-b border-gray-200' : ''}`}>
            <div className={`flex min-w-0 items-end gap-3 ${isDemandDistributionProfileTable ? 'w-full justify-between md:w-auto md:justify-start' : ''}`}>
              <h2 className="text-foreground truncate text-2xl font-semibold tracking-[-0.03em]">
                {isForecastDemandTable ? 'Forecast Demand' : activeView?.name || 'Demand Planning'}
              </h2>
              {selectedForecastDemandPlanning?.gross_basis === 'true_gross' && (
                <span className="rounded-full bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800">
                  True Gross Sales basis (converted {new Intl.DateTimeFormat('en-US').format(new Date(selectedForecastDemandPlanning?.basis_conversion?.converted_at || Date.now()))})
                </span>
              )}
              {isDemandDistributionProfileTable && (
                <button
                  type="button"
                  className="shrink-0 rounded-full border border-gray-300 border-border bg-white p-2 shadow-sm transition hover:bg-gray-50 md:hidden"
                  onClick={() => onOpenGovernanceDialog(activeView?.id)}
                  title="View governance"
                  aria-label="View governance"
                >
                  <InformationCircleIcon className="h-4 w-4" aria-hidden />
                </button>
              )}
              {isForecastDemandTable && (
                <button
                  className='flex items-center gap-2 bg-primary text-white hover:bg-gray-800 px-4 py-0.5 rounded-full font-bold text-sm whitespace-nowrap cursor-pointer'
                  onClick={() => {
                    setSelectedForecastDemandPlanning(null);
                    setActiveView(null);
                    clearFilters();
                    setSearchParams(
                      (sp) => {
                        const next = new URLSearchParams(sp);
                        next.delete('id');
                        next.delete('view');
                        clearFilterParamsFromSearchParams(next);
                        return next;
                      },
                      { replace: true }
                    );
                    urlHydratedForIdRef.current = null;
                  }}
                >
                  <span>Return to the list</span>
                  <ArrowUturnLeftIcon className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className={`flex-wrap items-center gap-2 justify-end ${isDemandDistributionProfileTable ? 'hidden md:flex' : 'flex'}`}>
              {access === 'edit' &&
                selectedForecastDemandPlanning?.gross_basis !== 'true_gross' &&
                (selectedForecastDemandPlanning?.budgetId || selectedForecastDemandPlanning?.connectedBudgetId) && (
                <button
                  type="button"
                  className="rounded-full border border-gray-300 bg-white px-3 py-1 text-xs font-semibold text-gray-700 hover:bg-gray-50"
                  onClick={handleConvertToTrueGross}
                >
                  Convert to true Gross Sales
                </button>
              )}
              {isForecastDemandTable && selectedForecastDemandPlanning?.id && sheetImportAccess && (
                <Menu as="div" className="relative">
                  <MenuButton aria-label="Forecast Demand actions" className="rounded-full border border-gray-300 bg-white p-1.5 text-gray-700 hover:bg-gray-50">
                    <EllipsisVerticalIcon className="h-5 w-5" />
                  </MenuButton>
                  <MenuItems anchor="bottom end" className="z-40 mt-1 w-64 rounded-md border border-gray-200 bg-white p-1 shadow-lg focus:outline-none">
                    <MenuItem>
                      <button type="button" onClick={() => setSheetImportOpen(true)} className="w-full rounded px-3 py-2 text-left text-sm data-[focus]:bg-gray-100">
                        Google Sheet link &amp; import
                      </button>
                    </MenuItem>
                  </MenuItems>
                </Menu>
              )}
              <DemandPlanningHeaderToolbar
                activePreset={activeView}
                isForecastDemandTable={isForecastDemandTable}
                onRefreshSalesSnapshot={handleRefreshSalesSnapshot}
                isRefreshingSales={isRefreshingSalesSnapshot}
                demandPlanningId={selectedForecastDemandPlanning?.id}
                demandPlanningYear={
                  effectiveForecastYear ||
                  selectedForecastDemandPlanning?.forecastYear ||
                  selectedForecastDemandPlanning?.forecast_year ||
                  null
                }
                onOpenGovernanceDialog={onOpenGovernanceDialog}
                showAdditionalButtons={showAdditionalButtons}
                hideColumnsButtonOnMobile={isDemandDistributionProfileTable}
                hideGovernanceButtonOnMobile={isDemandDistributionProfileTable}
                onOpenColumnsDrawer={openColumnsDrawer}
                withCustomColumns={activePresetColumnLayoutCustomized}
                marginFromSidebar={marginFromSidebar}
                drawerWidth={drawerWidth}
              />
              <ForecastDemandSheetImportModal
                planningId={selectedForecastDemandPlanning?.id}
                isOpen={sheetImportOpen}
                canEdit={sheetImportAccess === 'edit'}
                sheetLink={selectedForecastDemandPlanning?.data?.[String(selectedForecastDemandPlanning?.forecastYear || selectedForecastDemandPlanning?.forecast_year)]?.sheet_link}
                onClose={() => setSheetImportOpen(false)}
                onSaved={handleDemandPlanSheetImportApplied}
                onApplied={handleDemandPlanSheetImportApplied}
              />
            </div>
          </section>
          {showFilters && (
            <DemandPlanningFilters
              searchTerm={searchTerm}
              showUndoRestoreButton={showUndoRestoreButton}
              onOpenUndoRestore={onOpenUndoRestore}
              showChangeHistoryButton={showChangeHistoryButton}
              onOpenChangeHistory={() => setHistoryDrawerOpen(true)}
              onSearchChange={setSearchTerm}
              categoryFilterOptions={categoryFilterOptions}
              subcategoryFilterOptions={subcategoryFilterOptions}
              statusFilterOptions={productCatalogStatuses}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              isCategorySettingsLoading={isCategorySettingsLoading}
              onCategoryIdChange={setSelectedCategoryId}
              onSubcategoryIdChange={setSelectedSubcategoryId}
              onStatusIdChange={setSelectedStatusId}
              onClearFilters={clearFilters}
              activeViewId={activeView?.id}
              onSelectView={onSelectView}
              mobileFiltersSheetEnabled={isDemandDistributionProfileTable}
              mobileColumnOptions={ddpMobileColumnOptions}
              mobileVisibleColumnIds={ddpMobileVisibleColumnIds}
              onToggleMobileColumn={handleToggleDdpMobileColumn}
            />
          )}
          {isForecastDemandTable && (
            <ForecastDemandTable
              key={selectedForecastDemandPlanning?.id ?? 'none'}
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              salesSnapshotVersion={salesSnapshotVersion}
              onServerRecordUpdated={(item) => setSelectedForecastDemandPlanning(item)}
              onEffectiveForecastYearChange={(year) => {
                forecastDemandRefreshYearRef.current = year;
                setEffectiveForecastYear(year);
              }}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isPoScheduleTable && (
            <POScheduleTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              visibleColumnIds={resolvedVisibleColumnIds}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
            />
          )}
          {isReplacementDemandShiftTable && (
            <ReplacementDemandShiftTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isComponentDemandLedgerTable && (
            <ComponentDemandLedgerTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isInventoryCapitalPlanTable && (
            <InventoryCapitalPlanTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isBundleReplacementShiftTable && (
            <BundleReplacementShiftTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isPurchaseRoundingTable && (
            <PurchaseRoundingTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isContainerOptimizationTable && (
            <ContainerOptimizationTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isBundleDemandExplosionTable && (
            <BundleDemandExplosionTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isApprovedBuysTable && (
            <ApprovedBuysTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              marginFromSidebar={marginFromSidebar}
            />
          )}
          {isCashLiabilitiesTable && (
            <CashLiabilitiesTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              visibleColumnIds={resolvedVisibleColumnIds}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
            />
          )}
          {isAiRebalanceTable && (
            <AiRebalanceTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              searchTerm={searchTerm}
              onServerRecordUpdated={(item) => setSelectedForecastDemandPlanning(item)}
            />
          )}
          {isUpcomingBuysTable && (
            <UpcomingBuysTable
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              searchTerm={searchTerm}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              visibleColumnIds={resolvedVisibleColumnIds}
            />
          )}
          {isDemandDistributionProfileTable && (
            <DemandDistributionProfileTable
              startDistributionProfileDate={startDistributionProfileDate}
              endDistributionProfileDate={endDistributionProfileDate}
              handleStartDateChange={handleStartDateChange}
              handleEndDateChange={handleEndDateChange}
              handleRangeChange={handleDistributionProfileRangeChange}
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              categoryFilterOptions={categoryFilterOptions || []}
              subcategoryFilterOptions={subcategoryFilterOptions || []}
              searchTerm={searchTerm}
              selectedCategory={selectedCategoryId}
              selectedStatus={selectedStatusId}
              selectedSubcategory={selectedSubcategoryId}
              visibleColumnIds={resolvedVisibleColumnIds}
              mobileVisibleColumnIds={ddpMobileVisibleColumnIds}
            />
          )}
          {isDefaultTable && (
            <DefaultPlanningTable
              marginFromSidebar={marginFromSidebar}
              key={selectedForecastDemandPlanning?.id ?? 'none'}
              activeView={activeView}
              searchTerm={searchTerm}
              selectedForecastDemandPlanning={selectedForecastDemandPlanning}
              visibleColumnIds={resolvedVisibleColumnIds}
              selectedCategoryId={selectedCategoryId}
              selectedSubcategoryId={selectedSubcategoryId}
              selectedStatusId={selectedStatusId}
              overrideResetEpoch={overrideResetEpoch}
              historyDrawerOpen={historyDrawerOpen}
              onHistoryDrawerOpenChange={setHistoryDrawerOpen}
            />
          )}
          <SkuAllocationUndoRestoreModal
            isOpen={undoRestoreModalOpen}
            onClose={() => setUndoRestoreModalOpen(false)}
            demandPlanningId={selectedForecastDemandPlanning?.id}
            demandPlanningYear={
              selectedForecastDemandPlanning?.forecast_year ??
              selectedForecastDemandPlanning?.forecastYear
            }
            onApplied={refetchAfterUndoRestore}
          />
          <DemandPlanningColumnsSettings
            isOpen={columnDrawerOpen}
            onClose={() => setColumnDrawerOpen(false)}
            viewName={activeView?.name}
            viewId={activeView?.id}
            planningYear={demandPlanningYear}
            marginFromSidebar={marginFromSidebar}
            presetColumnIds={presetColumnIdsForActiveView}
            columns={columnDrawerDraft}
            onChangeColumns={setColumnDrawerDraft}
            onSaveCurrentView={handleSaveDemandPlanningColumns}
          />
          <ViewGovernanceDialog
            open={governanceDialogOpen}
            onOpenChange={open => {
              setGovernanceDialogOpen(open);
              if (!open) {
                setGovernanceViewId(null);
              }
            }}
            viewId={governancePreset?.id ?? activeView?.id}
            viewName={governancePreset?.name ?? activeView?.name ?? 'Demand Planning'}
            marginFromSidebar={marginFromSidebar}
          />
        </main>
      )}
    </>
  );
};

export default DemandPlanning;
